import type { FormEvent } from "react";
import {
  composeFamilyAccountDisplayNameFromStudentFamilyName,
  composeStudentDisplayNameFromParts,
  normalizeStudentNamePart,
  type AdminMembersResponseDto,
  type CreateAdminStudentRequestDto,
  type CreateFamilyAccountRequestDto,
  type FamilyAccountsResponseDto,
  type FamilyAccountStatus,
  type StudentStatus
} from "@madamuh/domain";
import { describeStatus } from "../../shared/lib/uiLabels";
import type { TenantLabels } from "../../shared/lib/tenantLabels";

export type FamilyAccountFormState = {
  displayName: string;
  primaryContactPlatformUserId: string;
  status: FamilyAccountStatus;
};

export type StudentFormState = {
  familyName: string;
  givenName: string;
  displayNameKana: string;
  birthDate: string;
  status: StudentStatus;
  familyAccountMode: "createNewFamilyAccount" | "useExistingFamilyAccount" | "none";
  familyAccountId: string;
  createFamilyAccountDisplayName: string;
  notesInternal: string;
};

export function createInitialFamilyAccountFormState(): FamilyAccountFormState {
  return {
    displayName: "",
    primaryContactPlatformUserId: "",
    status: "active"
  };
}

export function createInitialStudentFormState(): StudentFormState {
  return {
    familyName: "",
    givenName: "",
    displayNameKana: "",
    birthDate: "",
    status: "active",
    familyAccountMode: "createNewFamilyAccount",
    familyAccountId: "",
    createFamilyAccountDisplayName: "",
    notesInternal: ""
  };
}

export function getMemberDisplayName(member: AdminMembersResponseDto["items"][number]): string {
  return member.appNickname ?? member.lineDisplayNameSnapshot ?? "名前未設定";
}

export function getStudentDisplayNamePreview(form: StudentFormState): string {
  return composeStudentDisplayNameFromParts(form.familyName, form.givenName) ?? "";
}

export function buildCreateStudentRequest(form: StudentFormState): CreateAdminStudentRequestDto {
  const displayName = getStudentDisplayNamePreview(form);

  return {
    displayName,
    familyName: normalizeStudentNamePart(form.familyName),
    givenName: normalizeStudentNamePart(form.givenName),
    displayNameKana: form.displayNameKana.trim() || null,
    birthDate: form.birthDate,
    status: form.status,
    familyAccountId: form.familyAccountMode === "useExistingFamilyAccount" ? form.familyAccountId || null : null,
    createFamilyAccount:
      form.familyAccountMode === "createNewFamilyAccount"
        ? {
            displayName: form.createFamilyAccountDisplayName.trim(),
            status: "active"
          }
        : null,
    notesInternal: form.notesInternal.trim() || null
  };
}

export function buildCreateFamilyAccountRequest(form: FamilyAccountFormState): CreateFamilyAccountRequestDto {
  return {
    displayName: form.displayName.trim(),
    primaryContactPlatformUserId: form.primaryContactPlatformUserId || null,
    status: form.status
  };
}

export function syncFamilyAccountNameFromStudentFamilyName(
  form: StudentFormState,
  hasEditedCreateFamilyAccountDisplayName: boolean
): StudentFormState {
  if (hasEditedCreateFamilyAccountDisplayName || form.familyAccountMode !== "createNewFamilyAccount") {
    return form;
  }

  const nextDisplayName = composeFamilyAccountDisplayNameFromStudentFamilyName(form.familyName) ?? "";
  return nextDisplayName === form.createFamilyAccountDisplayName
    ? form
    : {
        ...form,
        createFamilyAccountDisplayName: nextDisplayName
      };
}

export function FamilyAccountCreateForm({
  form,
  members,
  tenantLabels,
  busy,
  onChange,
  onSubmit
}: {
  form: FamilyAccountFormState;
  members: AdminMembersResponseDto | null;
  tenantLabels: TenantLabels;
  busy: boolean;
  onChange: (form: FamilyAccountFormState) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <form
      onSubmit={onSubmit}
      style={{ display: "grid", gap: 12, padding: 12, border: "1px solid #e5e5e5", borderRadius: 12 }}
    >
      <label style={{ display: "grid", gap: 4 }}>
        <span>家族名</span>
        <input
          value={form.displayName}
          onChange={(event) => onChange({ ...form, displayName: event.target.value })}
          placeholder="佐藤様"
        />
      </label>

      <label style={{ display: "grid", gap: 4 }}>
        <span>代表保護者（連絡先）</span>
        <select
          value={form.primaryContactPlatformUserId}
          onChange={(event) => onChange({ ...form, primaryContactPlatformUserId: event.target.value })}
        >
          <option value="">未設定</option>
          {members?.items.map((member) => (
            <option key={member.platformUserId} value={member.platformUserId}>
              {getMemberDisplayName(member)}
            </option>
          ))}
        </select>
        <span style={{ fontSize: 12, opacity: 0.8 }}>
          代表保護者は家族の代表連絡先です。設定しただけでは{tenantLabels.student}の閲覧権限は付与されません。
          アプリで{tenantLabels.student}を確認するには、保護者紐付けが必要です。
        </span>
      </label>

      <label style={{ display: "grid", gap: 4 }}>
        <span>ステータス</span>
        <select
          value={form.status}
          onChange={(event) => onChange({ ...form, status: event.target.value === "inactive" ? "inactive" : "active" })}
        >
          <option value="active">{describeStatus("active")}</option>
          <option value="inactive">{describeStatus("inactive")}</option>
        </select>
      </label>

      <button type="submit" disabled={busy}>
        {busy ? "作成中..." : `${tenantLabels.familyAccount}を作成`}
      </button>
    </form>
  );
}

export function StudentCreateForm({
  form,
  familyAccounts,
  tenantLabels,
  busy,
  onChange,
  onEditCreateFamilyAccountName,
  onSubmit
}: {
  form: StudentFormState;
  familyAccounts: FamilyAccountsResponseDto | null;
  tenantLabels: TenantLabels;
  busy: boolean;
  onChange: (form: StudentFormState) => void;
  onEditCreateFamilyAccountName: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <form
      onSubmit={onSubmit}
      style={{ display: "grid", gap: 12, padding: 12, border: "1px solid #e5e5e5", borderRadius: 12 }}
    >
      <label style={{ display: "grid", gap: 4 }}>
        <span>姓</span>
        <input value={form.familyName} onChange={(event) => onChange({ ...form, familyName: event.target.value })} placeholder="湯木" />
      </label>

      <label style={{ display: "grid", gap: 4 }}>
        <span>名</span>
        <input value={form.givenName} onChange={(event) => onChange({ ...form, givenName: event.target.value })} placeholder="ひかり" />
      </label>

      <div style={{ display: "grid", gap: 4 }}>
        <span>表示名</span>
        <div style={{ padding: "10px 12px", border: "1px solid #e5e5e5", borderRadius: 10 }}>
          {getStudentDisplayNamePreview(form) || "姓と名を入力すると表示されます。"}
        </div>
      </div>

      <label style={{ display: "grid", gap: 4 }}>
        <span>表示名かな</span>
        <input
          value={form.displayNameKana}
          onChange={(event) => onChange({ ...form, displayNameKana: event.target.value })}
          placeholder="アンナ レビュー"
        />
      </label>

      <label style={{ display: "grid", gap: 4 }}>
        <span>生年月日</span>
        <input type="date" value={form.birthDate} onChange={(event) => onChange({ ...form, birthDate: event.target.value })} />
      </label>

      <label style={{ display: "grid", gap: 4 }}>
        <span>ステータス</span>
        <select
          value={form.status}
          onChange={(event) => onChange({ ...form, status: event.target.value === "inactive" ? "inactive" : "active" })}
        >
          <option value="active">{describeStatus("active")}</option>
          <option value="inactive">{describeStatus("inactive")}</option>
        </select>
      </label>

      <label style={{ display: "grid", gap: 4 }}>
        <span>{tenantLabels.familyAccount}の設定方法</span>
        <select
          value={form.familyAccountMode}
          onChange={(event) =>
            onChange({
              ...form,
              familyAccountMode:
                event.target.value === "useExistingFamilyAccount"
                  ? "useExistingFamilyAccount"
                  : event.target.value === "none"
                    ? "none"
                    : "createNewFamilyAccount"
            })
          }
        >
          <option value="createNewFamilyAccount">新しく作成する</option>
          <option value="useExistingFamilyAccount">既存の{tenantLabels.familyAccount}を使う</option>
          <option value="none">今は設定しない</option>
        </select>
      </label>

      {form.familyAccountMode === "createNewFamilyAccount" ? (
        <section
          style={{ display: "grid", gap: 12, padding: 12, border: "1px solid #e5e5e5", borderRadius: 12, background: "#fafafa" }}
        >
          <strong>新しい{tenantLabels.familyAccount}</strong>
          <label style={{ display: "grid", gap: 4 }}>
            <span>家族名</span>
            <input
              value={form.createFamilyAccountDisplayName}
              onChange={(event) => {
                onEditCreateFamilyAccountName();
                onChange({ ...form, createFamilyAccountDisplayName: event.target.value });
              }}
              placeholder="湯木様"
            />
          </label>
          <span style={{ fontSize: 12, opacity: 0.8 }}>家族名は姓から自動入力されます。必要に応じて修正できます。</span>
          <span style={{ fontSize: 12, opacity: 0.8 }}>同じ家族名が複数あっても問題ありません。内部IDで区別します。</span>
          <span style={{ fontSize: 12, opacity: 0.8 }}>
            {tenantLabels.familyAccount}は兄弟姉妹や将来の請求集計単位として使います。代表保護者（連絡先）はここでは未設定でも問題ありません。
          </span>
        </section>
      ) : null}

      {form.familyAccountMode === "useExistingFamilyAccount" ? (
        <label style={{ display: "grid", gap: 4 }}>
          <span>既存の{tenantLabels.familyAccount}</span>
          <select value={form.familyAccountId} onChange={(event) => onChange({ ...form, familyAccountId: event.target.value })}>
            <option value="">選択してください</option>
            {familyAccounts?.items.map((familyAccount) => (
              <option key={familyAccount.familyAccountId} value={familyAccount.familyAccountId}>
                {familyAccount.displayName} / {familyAccount.primaryContactDisplayNameSnapshot ?? "代表保護者（連絡先）未設定"}
              </option>
            ))}
          </select>
          <span style={{ fontSize: 12, opacity: 0.8 }}>同名の{tenantLabels.familyAccount}があり得るため、内部IDで紐付けます。</span>
        </label>
      ) : null}

      {form.familyAccountMode === "none" ? (
        <p style={{ margin: 0, fontSize: 12, opacity: 0.8 }}>
          後から兄弟姉妹や請求管理を行う場合は{tenantLabels.familyAccount}の設定を推奨します。
        </p>
      ) : null}

      <label style={{ display: "grid", gap: 4 }}>
        <span>内部メモ</span>
        <textarea
          value={form.notesInternal}
          onChange={(event) => onChange({ ...form, notesInternal: event.target.value })}
          rows={3}
          placeholder="内部メモ"
        />
      </label>

      <button type="submit" disabled={busy}>
        {busy ? "作成中..." : `${tenantLabels.student}を作成`}
      </button>
    </form>
  );
}
